import { openMessageAfter } from '../lib/whatsapp';
import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { useEffect, useState, useMemo } from 'react';
import moment from 'moment';
import 'moment/locale/tr';
import { WeddingCalendar } from '../components/calendar/WeddingCalendar';
import { useDataStore } from '../store/dataStore';
import { appointmentTypes, shootTypes, formatCurrency } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Loader2, 
  UserPlus, 
  Camera, 
  Video, 
  Plane, 
  TreeDeciduous, 
  Film, 
  BookOpen,
  Send,
  MessageSquare,
  User,
  ArrowRight
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';

const API = RUNTIME_BACKEND_URL;

moment.locale('tr');

const messages = {
  today: 'Bugün',
  previous: 'Geri',
  next: 'İleri',
  month: 'Ay',
  week: 'Hafta',
  day: 'Gün',
  agenda: 'Ajanda',
  date: 'Tarih',
  time: 'Saat',
  event: 'Etkinlik',
  noEventsInRange: 'Bu aralıkta etkinlik yok.',
  showMore: (total) => `+${total} daha`
};

// Türkiye Resmi ve Dini Tatilleri
const getTurkishHolidays = (year) => {
  const holidays = [
    // Sabit Resmi Tatiller
    { date: `${year}-01-01`, name: 'Yılbaşı', type: 'resmi' },
    { date: `${year}-04-23`, name: 'Ulusal Egemenlik ve Çocuk Bayramı', type: 'resmi' },
    { date: `${year}-05-01`, name: 'Emek ve Dayanışma Günü', type: 'resmi' },
    { date: `${year}-05-19`, name: 'Atatürk\'ü Anma, Gençlik ve Spor Bayramı', type: 'resmi' },
    { date: `${year}-07-15`, name: 'Demokrasi ve Milli Birlik Günü', type: 'resmi' },
    { date: `${year}-08-30`, name: 'Zafer Bayramı', type: 'resmi' },
    { date: `${year}-10-29`, name: 'Cumhuriyet Bayramı', type: 'resmi' },
  ];

  // Dini Tatiller (Hicri takvime göre değişir - 2025-2027 için yaklaşık tarihler)
  const religiousHolidays = {
    2025: [
      { date: '2025-03-30', name: 'Ramazan Bayramı (1. Gün)', type: 'dini' },
      { date: '2025-03-31', name: 'Ramazan Bayramı (2. Gün)', type: 'dini' },
      { date: '2025-04-01', name: 'Ramazan Bayramı (3. Gün)', type: 'dini' },
      { date: '2025-06-06', name: 'Kurban Bayramı (1. Gün)', type: 'dini' },
      { date: '2025-06-07', name: 'Kurban Bayramı (2. Gün)', type: 'dini' },
      { date: '2025-06-08', name: 'Kurban Bayramı (3. Gün)', type: 'dini' },
      { date: '2025-06-09', name: 'Kurban Bayramı (4. Gün)', type: 'dini' },
    ],
    2026: [
      { date: '2026-03-20', name: 'Ramazan Bayramı (1. Gün)', type: 'dini' },
      { date: '2026-03-21', name: 'Ramazan Bayramı (2. Gün)', type: 'dini' },
      { date: '2026-03-22', name: 'Ramazan Bayramı (3. Gün)', type: 'dini' },
      { date: '2026-05-27', name: 'Kurban Bayramı (1. Gün)', type: 'dini' },
      { date: '2026-05-28', name: 'Kurban Bayramı (2. Gün)', type: 'dini' },
      { date: '2026-05-29', name: 'Kurban Bayramı (3. Gün)', type: 'dini' },
      { date: '2026-05-30', name: 'Kurban Bayramı (4. Gün)', type: 'dini' },
    ],
    2027: [
      { date: '2027-03-09', name: 'Ramazan Bayramı (1. Gün)', type: 'dini' },
      { date: '2027-03-10', name: 'Ramazan Bayramı (2. Gün)', type: 'dini' },
      { date: '2027-03-11', name: 'Ramazan Bayramı (3. Gün)', type: 'dini' },
      { date: '2027-05-16', name: 'Kurban Bayramı (1. Gün)', type: 'dini' },
      { date: '2027-05-17', name: 'Kurban Bayramı (2. Gün)', type: 'dini' },
      { date: '2027-05-18', name: 'Kurban Bayramı (3. Gün)', type: 'dini' },
      { date: '2027-05-19', name: 'Kurban Bayramı (4. Gün)', type: 'dini' },
    ],
  };

  return [...holidays, ...(religiousHolidays[year] || [])];
};

// Tatil günü olup olmadığını kontrol et
const getHolidayInfo = (date, holidays) => {
  const dateStr = moment(date).format('YYYY-MM-DD');
  return holidays.find(h => h.date === dateStr);
};

// Hizmet seçenekleri
const serviceOptions = [
  { key: 'service_photo', label: 'Fotoğraf Çekimi', icon: Camera },
  { key: 'service_video', label: 'Video Çekimi', icon: Video },
  { key: 'service_drone', label: 'Drone Çekimi', icon: Plane },
  { key: 'service_outdoor', label: 'Dış Çekim', icon: TreeDeciduous },
  { key: 'service_clip', label: 'Klip Çekimi', icon: Film },
  { key: 'service_story', label: 'Hikaye Çekimi', icon: BookOpen },
];

export default function CalendarPage() {
  const { 
    calendarEvents, 
    calendarLoading, 
    fetchCalendar,
    customers,
    fetchCustomers,
    addCustomer,
    addAppointment,
    venues,
    fetchVenues,
    sendNotification
  } = useDataStore();
  
  const { token } = useAuthStore();
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('month');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [modalMode, setModalMode] = useState('select'); // select, new_customer, add_event, success, event_detail
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Ek gün formu
  const [eventForm, setEventForm] = useState({
    event_time_start: '',
    event_time_end: '',
    location: '',
    notes: '',
    // Hizmetler
    service_photo: false,
    service_video: false,
    service_drone: false,
    service_outdoor: false,
    service_clip: false,
    service_story: false
  });

  // New customer form with all fields
  const [customerForm, setCustomerForm] = useState({
    bride_name: '',
    groom_name: '',
    bride_phone: '',
    groom_phone: '',
    email: '',
    wedding_date: '',
    wedding_time_start: '',
    wedding_time_end: '',
    location: '',  // venue_id yerine location (elden yazılacak)
    notes: '',
    // Hizmetler
    service_photo: false,
    service_video: false,
    service_drone: false,
    service_outdoor: false,
    service_clip: false,
    service_story: false,
    // Ücret
    total_price: '',
    deposit_amount: ''
  });

  useEffect(() => {
    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();
    fetchCalendar(month, year);
  }, [currentDate, fetchCalendar]);

  useEffect(() => {
    if (customers.length === 0) fetchCustomers();
    if (venues.length === 0) fetchVenues();
  }, [customers.length, venues.length, fetchCustomers, fetchVenues]);

  // Tatil listesini hesapla
  const holidays = useMemo(() => {
    const year = currentDate.getFullYear();
    // Önceki ve sonraki yılları da dahil et (takvim görünümünde görünebilir)
    return [
      ...getTurkishHolidays(year - 1),
      ...getTurkishHolidays(year),
      ...getTurkishHolidays(year + 1)
    ];
  }, [currentDate]);

  // Tatil eventlerini oluştur
  const holidayEvents = useMemo(() => {
    return holidays.map((holiday, index) => {
      const date = new Date(holiday.date);
      return {
        id: `holiday-${index}`,
        title: holiday.name,
        start: date,
        end: date,
        allDay: true,
        resource: { 
          isHoliday: true, 
          holidayType: holiday.type,
          holidayName: holiday.name 
        }
      };
    });
  }, [holidays]);

  const events = useMemo(() => {
    const regularEvents = calendarEvents.map(event => {
      const startDate = new Date(event.date);
      if (event.time) {
        const [hours, minutes] = event.time.split(':');
        startDate.setHours(parseInt(hours), parseInt(minutes));
      }
      const endDate = new Date(startDate);
      endDate.setHours(endDate.getHours() + 2);

      let title = event.customer_name || 'Etkinlik';
      if (event.event_type === 'appointment') {
        title += ` - ${appointmentTypes[event.appointment_type] || 'Randevu'}`;
      } else if (event.event_type === 'shoot') {
        title += ` - ${shootTypes[event.shoot_type] || 'Çekim'}`;
      }

      return {
        id: event.id,
        title,
        start: startDate,
        end: endDate,
        resource: event
      };
    });
    
    // Etkinlik türü etiketleri
    const eventTypeLabels = {
      main: 'Düğün',
      shooting: 'Fotoğraf',
      video: 'Video',
      outdoor: 'Dış Çekim',
      ceremony: 'Nikah',
      henna: 'Kına',
      engagement: 'Nişan',
      other: 'Diğer'
    };
    
    // Müşteri events'lerini (tüm günleri) ekle
    const customerEvents = [];
    customers.forEach(customer => {
      const customerName = `${customer.bride_name} & ${customer.groom_name}`;
      
      // Events array'i varsa her birini ekle
      if (customer.events && customer.events.length > 0) {
        customer.events.forEach((event, index) => {
          const eventDate = new Date(event.event_date);
          if (event.event_time_start) {
            const [h, m] = event.event_time_start.split(':');
            eventDate.setHours(parseInt(h), parseInt(m));
          } else {
            eventDate.setHours(18, 0);
          }
          
          const endDate = new Date(eventDate);
          if (event.event_time_end) {
            const [h, m] = event.event_time_end.split(':');
            endDate.setHours(parseInt(h), parseInt(m));
          } else {
            endDate.setHours(23, 0);
          }
          
          const eventLabel = eventTypeLabels[event.event_type] || 'Etkinlik';
          const isMain = event.event_type === 'main';
          const timeStr = event.event_time_start ? 
            (event.event_time_end ? `${event.event_time_start}-${event.event_time_end}` : event.event_time_start) 
            : '';
          
          customerEvents.push({
            id: `customer-${customer.id}-event-${event.id || index}`,
            title: isMain 
              ? `${customerName}${timeStr ? ' ' + timeStr : ''}` 
              : `${customerName} - ${timeStr || eventLabel}`,
            start: eventDate,
            end: endDate,
            resource: { 
              ...customer,
              currentEvent: event,
              isCustomerWedding: true,
              isMainEvent: isMain,
              event_type: 'wedding'
            }
          });
        });
      } else if (customer.wedding_date) {
        // Eski format - events array'i yoksa wedding_date kullan
        const weddingDate = new Date(customer.wedding_date);
        if (customer.wedding_time_start) {
          const [h, m] = customer.wedding_time_start.split(':');
          weddingDate.setHours(parseInt(h), parseInt(m));
        } else {
          weddingDate.setHours(18, 0);
        }
        
        const endDate = new Date(weddingDate);
        if (customer.wedding_time_end) {
          const [h, m] = customer.wedding_time_end.split(':');
          endDate.setHours(parseInt(h), parseInt(m));
        } else {
          endDate.setHours(23, 0);
        }

        const timeStr = customer.wedding_time_start ?
          (customer.wedding_time_end ? `${customer.wedding_time_start}-${customer.wedding_time_end}` : customer.wedding_time_start)
          : '';

        customerEvents.push({
          id: `customer-${customer.id}`,
          title: `${customerName}${timeStr ? ' ' + timeStr : ''}`,
          start: weddingDate,
          end: endDate,
          resource: { 
            ...customer, 
            isCustomerWedding: true,
            isMainEvent: true,
            event_type: 'wedding'
          }
        });
      }
    });
    
    // Tatil eventlerini ekle
    return [...regularEvents, ...customerEvents, ...holidayEvents];
  }, [calendarEvents, customers, holidayEvents]);

  // Tatil günlerini işaretle
  const dayPropGetter = (date) => {
    const holiday = getHolidayInfo(date, holidays);
    if (holiday) {
      return {
        className: holiday.type === 'dini' ? 'holiday-religious' : 'holiday-official',
        style: {
          backgroundColor: holiday.type === 'dini' 
            ? 'rgba(34, 197, 94, 0.15)' // Yeşil - dini
            : 'rgba(239, 68, 68, 0.15)', // Kırmızı - resmi
        }
      };
    }
    return {};
  };

  const handleNavigate = (date) => {
    setCurrentDate(date);
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  // Mevcut etkinliğe tıklama
  const handleSelectEvent = (event) => {
    // Tatil eventlerine tıklamayı yoksay
    if (event.resource?.isHoliday) {
      return;
    }
    
    setSelectedEvent(event);
    setModalMode('event_detail');
    setIsModalOpen(true);
  };

  const handleSelectSlot = ({ start }) => {
    const dateStr = moment(start).format('YYYY-MM-DD');
    setSelectedDate(dateStr);
    setCustomerForm(prev => ({ 
      ...prev, 
      wedding_date: dateStr,
      bride_name: '',
      groom_name: '',
      bride_phone: '',
      groom_phone: '',
      email: '',
      wedding_time_start: '',
      wedding_time_end: '',
      location: '',
      notes: '',
      service_photo: false,
      service_video: false,
      service_drone: false,
      service_outdoor: false,
      service_clip: false,
      service_story: false,
      total_price: '',
      deposit_amount: ''
    }));
    setEventForm({
      event_time_start: '',
      event_time_end: '',
      location: '',
      notes: '',
      service_photo: false,
      service_video: false,
      service_drone: false,
      service_outdoor: false,
      service_clip: false,
      service_story: false
    });
    setModalMode('select'); // Önce seçim ekranı
    setSelectedCustomerId('');
    setSelectedEvent(null);
    setIsModalOpen(true);
  };

  const handleSelectExistingCustomer = (customerId) => {
    setSelectedCustomerId(customerId);
    setModalMode('add_event');
  };

  const handleAddNewCustomer = () => {
    setModalMode('new_customer');
  };

  // Mevcut müşteriye ek gün/etkinlik ekle
  const handleAddEventToCustomer = async () => {
    if (!selectedCustomerId || !selectedDate) return;
    
    // Seçilen hizmetleri listele
    const services = [];
    if (eventForm.service_photo) services.push('Fotoğraf');
    if (eventForm.service_video) services.push('Video');
    if (eventForm.service_drone) services.push('Drone');
    if (eventForm.service_outdoor) services.push('Dış Çekim');
    if (eventForm.service_clip) services.push('Klip');
    if (eventForm.service_story) services.push('Hikaye');
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/api/customers/${selectedCustomerId}/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          event_date: selectedDate,
          event_time_start: eventForm.event_time_start,
          event_time_end: eventForm.event_time_end,
          location: eventForm.location,
          services: services,
          notes: eventForm.notes
        })
      });
      
      if (!response.ok) throw new Error('Etkinlik eklenemedi');
      
      toast.success('Çekim günü eklendi');
      setIsModalOpen(false);
      fetchCustomers(); // Müşterileri yenile
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      fetchCalendar(month, year);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEventServiceChange = (serviceKey, checked) => {
    setEventForm(prev => ({ ...prev, [serviceKey]: checked }));
  };

  const handleServiceChange = (serviceKey, checked) => {
    setCustomerForm(prev => ({ ...prev, [serviceKey]: checked }));
  };

  const handleCreateCustomer = async () => {
    if (!customerForm.bride_name || !customerForm.groom_name || !customerForm.bride_phone) {
      toast.error('Gelin adı, damat adı ve gelin telefonu zorunludur');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...customerForm,
        total_price: parseFloat(customerForm.total_price) || 0,
        deposit_amount: parseFloat(customerForm.deposit_amount) || 0
      };
      
      const newCustomer = await addCustomer(payload);
      toast.success('Müşteri başarıyla eklendi!');
      setSelectedCustomerId(newCustomer.id);
      setModalMode('success');
    } catch (error) {
      toast.error(error.message || 'Müşteri eklenemedi');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendAgreement = async (channel) => {
    if (!selectedCustomerId) return;
    
    setIsSubmitting(true);
    try {
      const API = RUNTIME_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      await openMessageAfter(channel, async () => {
        const response = await fetch(`${API}/api/notifications/send-agreement`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            customer_id: selectedCustomerId,
            channel: channel
          })
        });
        if (!response.ok) throw new Error('Mesaj hazırlanamadı');
        const data = await response.json();
        return { phone: data.to, text: data.content };
      });
      toast.success(`${channel === 'sms' ? 'SMS' : 'WhatsApp'} açıldı — anlaşma mesajı hazır`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const eventStyleGetter = (event) => {
    // Tatil eventi
    if (event.resource?.isHoliday) {
      const isReligious = event.resource.holidayType === 'dini';
      return {
        style: {
          backgroundColor: isReligious ? 'hsl(142, 71%, 45%)' : 'hsl(0, 84%, 60%)',
          borderRadius: '4px',
          opacity: 0.85,
          color: 'white',
          border: 'none',
          fontSize: '11px',
          fontWeight: '500'
        }
      };
    }
    
    // Müşteri düğünü
    if (event.resource?.isCustomerWedding) {
      return {
        style: {
          backgroundColor: 'hsl(280, 60%, 55%)', // Mor - düğün
          borderRadius: '6px',
          opacity: 0.95,
          color: 'white',
          border: 'none',
          fontSize: '12px',
          fontWeight: '500'
        }
      };
    }
    
    // Normal event (randevu/çekim)
    const isAppointment = event.resource?.event_type === 'appointment';
    const backgroundColor = isAppointment ? 'hsl(346, 60%, 55%)' : 'hsl(200, 70%, 50%)';
    
    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        opacity: 0.9,
        color: 'white',
        border: 'none',
        fontSize: '12px'
      }
    };
  };
  
  // Kalan tutar hesaplama
  const remainingAmount = (parseFloat(customerForm.total_price) || 0) - (parseFloat(customerForm.deposit_amount) || 0);

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Takvim</h1>
        <p className="text-muted-foreground mt-1">
          Bir güne dokunun, o günün işlerini görün; "Ekle" ile yeni müşteri veya çekim ekleyin
        </p>
      </div>

      {calendarLoading && !events.length ? (
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <WeddingCalendar
          date={currentDate}
          onNavigate={handleNavigate}
          events={events}
          holidayOf={(d) => getHolidayInfo(d, holidays)}
          onOpenEvent={handleSelectEvent}
          onAddToDay={(d) => handleSelectSlot({ start: d })}
        />
      )}

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {modalMode === 'select' && `${moment(selectedDate).format('DD MMMM YYYY')} - İşlem Seçin`}
              {modalMode === 'new_customer' && `${moment(selectedDate).format('DD MMMM YYYY')} - Yeni Müşteri Ekle`}
              {modalMode === 'add_event' && `${moment(selectedDate).format('DD MMMM YYYY')} - Etkinlik Ekle`}
              {modalMode === 'success' && 'Müşteri Eklendi ✓'}
              {modalMode === 'event_detail' && 'Etkinlik Detayı'}
            </DialogTitle>
          </DialogHeader>

          {/* Select Mode - Mevcut veya Yeni */}
          {modalMode === 'select' && (
            <div className="space-y-4">
              <p className="text-muted-foreground text-sm">Bu tarih için ne yapmak istiyorsunuz?</p>
              
              <Button 
                className="w-full justify-start h-auto py-4" 
                variant="outline"
                onClick={handleAddNewCustomer}
              >
                <UserPlus className="h-5 w-5 mr-3 text-primary" />
                <div className="text-left">
                  <p className="font-medium">Yeni Müşteri Ekle</p>
                  <p className="text-xs text-muted-foreground">Yeni bir müşteri kaydı oluştur</p>
                </div>
              </Button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">veya mevcut müşteriye ek gün ekle</span>
                </div>
              </div>
              
              {customers.length > 0 ? (
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {customers.map((customer) => (
                    <div
                      key={customer.id}
                      className="flex items-center justify-between p-3 rounded-lg border hover:border-primary hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => handleSelectExistingCustomer(customer.id)}
                    >
                      <div>
                        <p className="font-medium">{customer.bride_name} & {customer.groom_name}</p>
                        <p className="text-xs text-muted-foreground">
                          Ana Tarih: {moment(customer.wedding_date).format('DD MMM YYYY')}
                          {customer.events?.length > 1 && ` • ${customer.events.length} gün`}
                        </p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">Henüz müşteri yok</p>
              )}
            </div>
          )}

          {/* Add Event to Existing Customer */}
          {modalMode === 'add_event' && selectedCustomerId && (
            <div className="space-y-4">
              {(() => {
                const customer = customers.find(c => c.id === selectedCustomerId);
                return customer ? (
                  <>
                    <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                      <p className="font-medium text-primary">{customer.bride_name} & {customer.groom_name}</p>
                      <p className="text-xs text-muted-foreground">{customer.bride_phone}</p>
                    </div>
                    
                    <div className="space-y-4">
                      {/* Saat */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Başlangıç Saati</Label>
                          <Input
                            type="time"
                            value={eventForm.event_time_start}
                            onChange={(e) => setEventForm(prev => ({ ...prev, event_time_start: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Bitiş Saati</Label>
                          <Input
                            type="time"
                            value={eventForm.event_time_end}
                            onChange={(e) => setEventForm(prev => ({ ...prev, event_time_end: e.target.value }))}
                          />
                        </div>
                      </div>
                      
                      {/* Yer / Mekan */}
                      <div className="space-y-2">
                        <Label>Yer / Mekan</Label>
                        <Input
                          value={eventForm.location}
                          onChange={(e) => setEventForm(prev => ({ ...prev, location: e.target.value }))}
                          placeholder="Çekim yapılacak yer..."
                        />
                      </div>
                      
                      {/* Hizmetler */}
                      <div className="space-y-2">
                        <Label>Hizmetler</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { key: 'service_photo', label: 'Fotoğraf', icon: Camera },
                            { key: 'service_video', label: 'Video', icon: Video },
                            { key: 'service_drone', label: 'Drone', icon: Plane },
                            { key: 'service_outdoor', label: 'Dış Çekim', icon: TreeDeciduous },
                            { key: 'service_clip', label: 'Klip', icon: Film },
                            { key: 'service_story', label: 'Hikaye', icon: BookOpen }
                          ].map(({ key, label, icon: Icon }) => (
                            <div key={key} className="flex items-center space-x-2 p-2 rounded border hover:bg-muted/50">
                              <Checkbox
                                id={`event-${key}`}
                                checked={eventForm[key]}
                                onCheckedChange={(checked) => handleEventServiceChange(key, checked)}
                              />
                              <Icon className="h-4 w-4 text-muted-foreground" />
                              <label htmlFor={`event-${key}`} className="text-sm cursor-pointer">{label}</label>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Notlar */}
                      <div className="space-y-2">
                        <Label>Notlar</Label>
                        <Textarea
                          value={eventForm.notes}
                          onChange={(e) => setEventForm(prev => ({ ...prev, notes: e.target.value }))}
                          placeholder="Bu gün için notlar..."
                          rows={2}
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-3 pt-2">
                      <Button variant="outline" onClick={() => setModalMode('select')} className="flex-1">
                        Geri
                      </Button>
                      <Button onClick={handleAddEventToCustomer} disabled={isSubmitting} className="flex-1">
                        {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Etkinlik Ekle
                      </Button>
                    </div>
                  </>
                ) : null;
              })()}
            </div>
          )}

          {/* Event Detail View */}
          {modalMode === 'event_detail' && selectedEvent && (
            <div className="space-y-4">
              {/* Event Info */}
              <div className="bg-muted/50 p-4 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">{selectedEvent.title}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    selectedEvent.resource?.isCustomerWedding
                      ? 'bg-purple-500/20 text-purple-600'
                      : selectedEvent.resource?.event_type === 'appointment' 
                        ? 'bg-primary/20 text-primary' 
                        : 'bg-blue-500/20 text-blue-600'
                  }`}>
                    {selectedEvent.resource?.isCustomerWedding 
                      ? 'Düğün' 
                      : selectedEvent.resource?.event_type === 'appointment' 
                        ? 'Randevu' 
                        : 'Çekim'}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Tarih:</span>
                    <p className="font-medium">{moment(selectedEvent.start).format('DD MMMM YYYY')}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Saat:</span>
                    <p className="font-medium">
                      {moment(selectedEvent.start).format('HH:mm')}
                      {selectedEvent.resource?.event_time_end && ` - ${selectedEvent.resource.event_time_end}`}
                    </p>
                  </div>
                  {selectedEvent.resource?.isCustomerWedding && (
                    <>
                      <div>
                        <span className="text-muted-foreground">Gelin:</span>
                        <p className="font-medium">{selectedEvent.resource.bride_name}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Damat:</span>
                        <p className="font-medium">{selectedEvent.resource.groom_name}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Telefon:</span>
                        <p className="font-medium">{selectedEvent.resource.bride_phone}</p>
                      </div>
                      {selectedEvent.resource?.total_price > 0 && (
                        <div>
                          <span className="text-muted-foreground">Toplam:</span>
                          <p className="font-medium text-primary">
                            {selectedEvent.resource.total_price?.toLocaleString('tr-TR')} TL
                          </p>
                        </div>
                      )}
                    </>
                  )}
                  {selectedEvent.resource?.appointment_type && (
                    <div>
                      <span className="text-muted-foreground">Tür:</span>
                      <p className="font-medium">{appointmentTypes[selectedEvent.resource.appointment_type]}</p>
                    </div>
                  )}
                  {selectedEvent.resource?.shoot_type && (
                    <div>
                      <span className="text-muted-foreground">Çekim Türü:</span>
                      <p className="font-medium">{shootTypes[selectedEvent.resource.shoot_type]}</p>
                    </div>
                  )}
                  {selectedEvent.resource?.location && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Konum:</span>
                      <p className="font-medium">{selectedEvent.resource.location}</p>
                    </div>
                  )}
                  {selectedEvent.resource?.notes && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Notlar:</span>
                      <p className="font-medium">{selectedEvent.resource.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                {(selectedEvent.resource?.customer_id || selectedEvent.resource?.isCustomerWedding) && (
                  <Button 
                    className="flex-1"
                    onClick={() => {
                      setIsModalOpen(false);
                      const customerId = selectedEvent.resource?.customer_id || selectedEvent.resource?.id;
                      window.location.href = `/panel/musteri/${customerId}`;
                    }}
                  >
                    <User className="h-4 w-4 mr-2" />
                    Müşteri Detayı
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setIsModalOpen(false)}
                >
                  Kapat
                </Button>
              </div>
            </div>
          )}

          {/* New Customer Form */}
          {modalMode === 'new_customer' && (
            <div className="space-y-6">
              {/* Kişi Bilgileri */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Kişi Bilgileri</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Ad *</Label>
                    <Input
                      value={customerForm.bride_name}
                      onChange={(e) => setCustomerForm({ ...customerForm, bride_name: e.target.value })}
                      placeholder="Ad"
                      data-testid="cal-bride-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ad *</Label>
                    <Input
                      value={customerForm.groom_name}
                      onChange={(e) => setCustomerForm({ ...customerForm, groom_name: e.target.value })}
                      placeholder="Ad"
                      data-testid="cal-groom-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tel 1 *</Label>
                    <Input
                      value={customerForm.bride_phone}
                      onChange={(e) => setCustomerForm({ ...customerForm, bride_phone: e.target.value })}
                      placeholder="05XX XXX XX XX"
                      data-testid="cal-bride-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tel 2</Label>
                    <Input
                      value={customerForm.groom_phone}
                      onChange={(e) => setCustomerForm({ ...customerForm, groom_phone: e.target.value })}
                      placeholder="05XX XXX XX XX"
                      data-testid="cal-groom-phone-input"
                    />
                  </div>
                </div>
              </div>

              {/* Düğün Bilgileri */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Düğün Bilgileri</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Düğün Tarihi</Label>
                    <Input
                      type="date"
                      value={customerForm.wedding_date}
                      onChange={(e) => setCustomerForm({ ...customerForm, wedding_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Yer / Mekan</Label>
                    <Input
                      value={customerForm.location}
                      onChange={(e) => setCustomerForm({ ...customerForm, location: e.target.value })}
                      placeholder="Düğün / çekim yeri..."
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Başlangıç Saati</Label>
                    <Input
                      type="time"
                      value={customerForm.wedding_time_start}
                      onChange={(e) => setCustomerForm({ ...customerForm, wedding_time_start: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Bitiş Saati</Label>
                    <Input
                      type="time"
                      value={customerForm.wedding_time_end}
                      onChange={(e) => setCustomerForm({ ...customerForm, wedding_time_end: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Hizmet Seçimleri */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Hizmetler</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {serviceOptions.map(({ key, label, icon: Icon }) => (
                    <label
                      key={key}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        customerForm[key] 
                          ? 'bg-primary/10 border-primary' 
                          : 'bg-muted/30 border-border hover:bg-muted/50'
                      }`}
                    >
                      <Checkbox
                        checked={customerForm[key]}
                        onCheckedChange={(checked) => handleServiceChange(key, checked)}
                      />
                      <Icon className={`h-4 w-4 ${customerForm[key] ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className={`text-sm ${customerForm[key] ? 'font-medium' : ''}`}>{label}</span>
                    </label>
                  ))}
                </div>
                <div className="space-y-2">
                  <Label>Manuel Hizmet</Label>
                  <Input
                    value={customerForm.custom_services || ''}
                    onChange={(e) => setCustomerForm({ ...customerForm, custom_services: e.target.value })}
                    placeholder="Listede olmayan ek hizmetler..."
                  />
                </div>
              </div>

              {/* Ücret Bilgileri */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Ücret Bilgileri</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Toplam Tutar (TL)</Label>
                    <Input
                      type="number"
                      value={customerForm.total_price}
                      onChange={(e) => setCustomerForm({ ...customerForm, total_price: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Kapora (TL)</Label>
                    <Input
                      type="number"
                      value={customerForm.deposit_amount}
                      onChange={(e) => setCustomerForm({ ...customerForm, deposit_amount: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Kalan Tutar (TL)</Label>
                    <div className="h-10 px-3 py-2 rounded-md border bg-muted/50 flex items-center font-semibold text-primary">
                      {remainingAmount.toLocaleString('tr-TR')} TL
                    </div>
                  </div>
                </div>
              </div>

              {/* Notlar */}
              <div className="space-y-2">
                <Label>Notlar</Label>
                <Textarea
                  value={customerForm.notes}
                  onChange={(e) => setCustomerForm({ ...customerForm, notes: e.target.value })}
                  placeholder="Ek notlar, özel istekler..."
                  rows={3}
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setIsModalOpen(false)} className="flex-1">
                  İptal
                </Button>
                <Button onClick={handleCreateCustomer} disabled={isSubmitting} className="flex-1" data-testid="cal-create-customer-btn">
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Müşteri Ekle'}
                </Button>
              </div>
            </div>
          )}

          {/* Success - Send Agreement */}
          {modalMode === 'success' && (
            <div className="space-y-6">
              <div className="text-center py-4">
                <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                  <UserPlus className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Müşteri Başarıyla Eklendi!</h3>
                <p className="text-muted-foreground text-sm">
                  Müşteri takvime eklendi. Şimdi anlaşma bilgilerini mesaj olarak gönderebilirsiniz.
                </p>
              </div>

              {/* Summary */}
              <div className="p-4 rounded-lg bg-muted/50 space-y-2 text-sm">
                <p><strong>Müşteri:</strong> {customerForm.bride_name} & {customerForm.groom_name}</p>
                <p><strong>Telefon:</strong> {customerForm.bride_phone}</p>
                <p><strong>Düğün Tarihi:</strong> {customerForm.wedding_date ? moment(customerForm.wedding_date).format('DD MMMM YYYY') : '-'}</p>
                <p><strong>Toplam:</strong> {parseFloat(customerForm.total_price || 0).toLocaleString('tr-TR')} TL</p>
                <p><strong>Kapora:</strong> {parseFloat(customerForm.deposit_amount || 0).toLocaleString('tr-TR')} TL</p>
                <p><strong>Kalan:</strong> <span className="text-primary font-semibold">{remainingAmount.toLocaleString('tr-TR')} TL</span></p>
              </div>

              {/* Send Agreement Buttons */}
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground text-center">Anlaşma mesajı gönder:</p>
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => handleSendAgreement('sms')}
                    disabled={isSubmitting}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    SMS Gönder
                  </Button>
                  <Button 
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => handleSendAgreement('whatsapp')}
                    disabled={isSubmitting}
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    WhatsApp
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  ⚠️ Mesajda: Hizmetler, tutar bilgileri, çekim bitiminde ödeme ve gecikmeli ödemede %5 fark bilgisi yer alacaktır.
                </p>
              </div>

              {/* Close Button */}
              <div className="pt-2">
                <Button onClick={() => setIsModalOpen(false)} className="w-full">
                  Tamam
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
